import React, { useEffect, useRef, useState } from 'react';
import { useSphere } from '@react-three/cannon';
import { useFrame } from '@react-three/fiber';
import { Trail } from '@react-three/drei';
import * as THREE from 'three';

const ActiveBall = React.forwardRef(({ id, position, onReset, onZoneChange, onDrain, onStuck, isLocked, lockPosition }, ref) => {
    const [sphereRef, api] = useSphere(() => ({
        mass: 2, // Standard Steel
        args: [0.48],
        position,
        material: { friction: 0.001, restitution: 0.5 }, // Low friction
        linearDamping: 0.0, // Zero Drag
        angularDamping: 0.0, // Infinite Spin
        fixedRotation: false,
        allowSleep: false,
        collisionFilterGroup: 1,
        collisionFilterMask: -1,
        userData: { id, name: 'Ball' } // Add ID for Multiball tracking
    }));

    const velocity = useRef(new THREE.Vector3());
    const positionRef = useRef(new THREE.Vector3());
    const stuckTimer = useRef(0);
    const inPlungerZone = useRef(true);
    const hasDrained = useRef(false);

    // Expose Launch API to Parent
    React.useImperativeHandle(ref, () => ({
        launch: () => {
            const { x, z } = positionRef.current;
            // Launch zone check (Must be in plunger lane)
            if (x > 6 && z > 5) {
                api.velocity.set(0, 0, -60); // Powerful launch
                api.wakeUp();
                return true; // Launch successful
            }
            return false;
        },
        reset: () => {
            api.position.set(...position);
            api.velocity.set(0, 0, 0);
            api.angularVelocity.set(0, 0, 0);
            hasDrained.current = false;
        },
        eject: (force) => {
            api.applyImpulse(force, [0, 0, 0]);
        }
    }));

    useEffect(() => {
        const unsubVel = api.velocity.subscribe((v) => velocity.current.set(...v));
        const unsubPos = api.position.subscribe((p) => positionRef.current.set(...p));
        return () => {
            unsubVel();
            unsubPos();
        };
    }, [api]);

    // Manual Launch Control (Keyboard Fallback)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                const { x, z } = positionRef.current;
                if (x > 6 && z > 5) {
                    api.velocity.set(0, 0, -60);
                    api.wakeUp();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [api]);

    useFrame((state, delta) => {
        // Locking Logic
        if (isLocked && lockPosition) {
            api.position.set(...lockPosition);
            api.velocity.set(0, 0, 0);
            api.angularVelocity.set(0, 0, 0);
            return;
        }

        // 1. Drain Check
        if (positionRef.current.y < -10) {
            if (!hasDrained.current) {
                hasDrained.current = true;
                onDrain(id);
            }
            return;
        }

        // 2. Zone Detection (Plunger Lane)
        // Plunger Lane is roughly x > 6 and z > 0
        const isNowInPlunger = positionRef.current.x > 6 && positionRef.current.z > 0;
        if (isNowInPlunger !== inPlungerZone.current) {
            inPlungerZone.current = isNowInPlunger;
            if (onZoneChange) onZoneChange(isNowInPlunger, id);
        }

        // 3. Stuck Watchdog
        // If velocity is very low AND we are not in the plunger lane (x > 6)
        const speed = velocity.current.length();

        if (speed < 0.1 && !isNowInPlunger && !isLocked) {
            stuckTimer.current += delta;
            if (stuckTimer.current > 4.0) {
                // Ball is stuck for 4 seconds
                if (onStuck) onStuck(id);
            }
        } else {
            stuckTimer.current = 0;
            // If we started moving, clear stuck status (handled by parent usually, but good to reset timer)
        }
    });

    return (
        <Trail
            width={5}
            length={8}
            color="#FF00FF" // Hot Pink Trail
            attenuation={(t) => t * t}
        >
            <mesh ref={sphereRef} castShadow={true} receiveShadow={false} name="Ball">
                <sphereGeometry args={[0.48, 32, 32]} />
                <meshStandardMaterial
                    color="#00ffff"
                    metalness={0.4}
                    roughness={0.3}
                    emissive="#ffffff"
                    emissiveIntensity={0.2}
                />
            </mesh>
        </Trail>
    );
});

export default ActiveBall;
